/**
 * Small, pure display helpers for the AdvancedMD-familiar proposal.
 *
 * Kept separate from the presentational components so the date/sig/prescriber
 * formatting is trivially unit-testable and reused across rows.
 */

import type {
  DatePrecision,
  MedicationEpisodeVM,
  MedicationOrderVM,
  RegimenInterval,
  TimelineEventVM,
} from './types';

const EM_DASH = '—';

const fullDate = new Intl.DateTimeFormat('en-US', {
  month: 'short',
  day: 'numeric',
  year: 'numeric',
  timeZone: 'UTC',
});

const monthYear = new Intl.DateTimeFormat('en-US', {
  month: 'short',
  year: 'numeric',
  timeZone: 'UTC',
});

/** Format an exact ISO date, falling back to an em dash when absent/invalid. */
export const formatIsoDate = (iso: string | null | undefined): string => {
  if (!iso) return EM_DASH;
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return EM_DASH;
  return fullDate.format(date);
};

/** Format a DatePrecision, honoring month / year / approximate precision. */
export const formatPrecisionDate = (dp: DatePrecision | undefined): string => {
  if (!dp?.value) return 'Unknown';
  const date = new Date(dp.value);
  if (Number.isNaN(date.getTime())) return dp.value;
  switch (dp.precision) {
    case 'year':
      return String(date.getUTCFullYear());
    case 'month':
      return monthYear.format(date);
    case 'approximate':
      return `~${monthYear.format(date)}`;
    default:
      return fullDate.format(date);
  }
};

/** Timeline events carry an exact ISO date plus an optional precision hint. */
export const formatEventDate = (event: TimelineEventVM): string =>
  formatPrecisionDate({ value: event.date, precision: event.datePrecision ?? 'exact' });

/** "150 mg · once daily (morning)" from the regimen that best reflects reality. */
export const composeSig = (regimen: RegimenInterval | undefined): string => {
  if (!regimen) return EM_DASH;
  return `${regimen.doseText} · ${regimen.frequency}`;
};

/** Split a pharmacy display string ("Walgreens #03782") into name + store number. */
export const splitPharmacy = (
  pharmacy: string,
): { name: string; store: string | null } => {
  const hashIndex = pharmacy.indexOf('#');
  if (hashIndex <= 0) return { name: pharmacy.trim(), store: null };
  return {
    name: pharmacy.slice(0, hashIndex).trim(),
    store: pharmacy.slice(hashIndex).trim(),
  };
};

/** Bare refill count — the column header carries the unit; unknown stays a dash. */
export const formatRefills = (order: MedicationOrderVM | undefined): string => {
  const refills = order?.refills;
  if (refills === null || refills === undefined) return EM_DASH;
  return String(refills);
};

/**
 * The prescriber to show in the "Sent by" column. Prefers the current order's
 * prescriber, then the earliest timeline event that names one, then the
 * managing clinician (covers outside / patient-reported medications).
 */
export const resolvePrescriber = (episode: MedicationEpisodeVM): string | null => {
  const orderPrescriber = episode.orders[0]?.prescriber;
  if (orderPrescriber) return orderPrescriber;
  const eventPrescriber = episode.timeline.find((event) => event.prescriber)?.prescriber;
  if (eventPrescriber) return eventPrescriber;
  return episode.managingClinician ?? null;
};

const INACTIVE_STATUSES: ReadonlySet<string> = new Set([
  'stopped',
  'completed',
  'historical',
  'uncertain',
]);

export const isInactiveEpisode = (episode: MedicationEpisodeVM): boolean =>
  INACTIVE_STATUSES.has(episode.episodeStatus);

/**
 * The single date + caption for the "Date" column. Ordered meds show their last
 * fill; stopped meds show when they ended; everything else shows when it began.
 */
export const resolveDateCell = (
  episode: MedicationEpisodeVM,
): { value: string; caption: string } => {
  const regimen = episode.actualUse ?? episode.prescribed;
  const order = episode.orders[0];

  if (isInactiveEpisode(episode) && regimen?.end?.value) {
    return { value: formatPrecisionDate(regimen.end), caption: 'stopped' };
  }
  if (order?.lastFillDate) {
    return { value: formatIsoDate(order.lastFillDate), caption: 'last filled' };
  }
  if (regimen?.start?.value) {
    return { value: formatPrecisionDate(regimen.start), caption: 'started' };
  }
  return { value: EM_DASH, caption: '' };
};

/** Timeline kinds that read as prescription history (drops measures / notes). */
const PRESCRIPTION_KINDS: ReadonlySet<TimelineEventVM['kind']> = new Set([
  'started',
  'increased',
  'decreased',
  'use_changed',
  'sent',
  'renewed',
  'cancelled',
  'fill_recorded',
  'held',
  'restarted',
  'taper',
  'stopped',
  'completed',
  'administration',
]);

/** Past-prescription rows, newest first, excluding pure measurement noise. */
export const prescriptionHistory = (episode: MedicationEpisodeVM): TimelineEventVM[] =>
  episode.timeline
    .filter((event) => PRESCRIPTION_KINDS.has(event.kind))
    .slice()
    .sort((a, b) => b.date.localeCompare(a.date));

export const patientFirstName = (fullName: string): string =>
  fullName.trim().split(/\s+/)[0] ?? fullName;
